import { useCallback, useMemo, FC } from "react";
import dayjs from "dayjs";
import { Field, Form, Formik } from "formik";
import {
  Delete,
  Info,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Paper,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import DialogVersion from "../../../components/dialog/dialog.component";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import { Forecast, ForecastType, FORECAST_TYPE_LABELS } from "../../../models/general/forecast";
import { Customer } from "../../../models/general/customer";
import { Bom } from "../../../models/general/bom";
import AutocompleteSimple from "../../../components/autocomplete/autocomplete";
import { BaseService } from "../../../services/base-service";
import { URLS } from "../../../services/app-urls";
import { translate } from "../../../translate/translate";
import { useForecastPage } from "./useForecastPage";
import Loading from "../../../components/loading/loading";
import ForecastTypeBadge from "../../../components/forecast/forecast-type-badge.component";

const ForecastPageComponent: FC = () => {
  const serviceCustomer = new BaseService<Customer>(URLS.CUSTOMER);
  const serviceBom = new BaseService<Bom>(URLS.BOM_FINISHED_GOOD);

  const {
    dataSource,
    pagination,
    rowsPerPage,
    page,
    searchDescription,
    forecastType,
    openDeleteModal,
    selectedIds,
    isLoading,
    confirmDeleteMessage,
    toggleSelectOne,
    toggleSelectAll,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRowClick,
    handleCloseDeleteModal,
    handleOpenDeleteModal,
    handleSearchChange,
    handleCustomerChange,
    handleFinishedGoodChange,
    handleForecastTypeChange,
    search,
    DISPLAYED_COLUMNS_SHOW,
    DISPLAYED_COLUMNS,
    SHOW_ACTIONS,
    DATE_COLUMNS,
    COLUMN_STYLES,
  } = useForecastPage();

  const handleSelectCustomer = useCallback(
    (customer: Customer) => {
      handleCustomerChange(customer?.name || "");
    },
    [handleCustomerChange]
  );

  const handleSelectFinishedGood = useCallback(
    (bom: Bom) => {
      handleFinishedGoodChange(bom?.bom_number || "");
    },
    [handleFinishedGoodChange]
  );

  const renderCreatedAtCell = useCallback((row: any) => {
    const cleanedDateTime = String(row.creation_date).replace(/\.\d+/, "");
    const timeFormat = translate.language === "pt" ? "HH:mm" : "hh:mm A";
    const formattedDate = dayjs(cleanedDateTime).format("L");
    const formattedTime = dayjs(cleanedDateTime).format(timeFormat);
    return `${formattedDate} ${formattedTime}`;
  }, []);

  const renderCheckboxCell = useCallback(
    (row: Forecast) => {
      const isDisabled = row.has_analysis === true;

      return (
        <Box display="flex" sx={{ height: "100%" }}>
          <Checkbox
            disabled={isDisabled}
            checked={selectedIds.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelectOne(row.id)}
            color="primary"
            sx={{
              padding: 0.5,
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          />
        </Box>
      );
    },
    [selectedIds, toggleSelectOne]
  );

  const renderHeaderCheckbox = useCallback(
    () => (
      <Box display="flex" sx={{ height: "100%" }}>
        <Checkbox
          indeterminate={
            selectedIds.length > 0 &&
            selectedIds.length < dataSource.filter((f) => !f.has_analysis).length
          }
          checked={
            selectedIds.length ===
            dataSource.filter((f) => !f.has_analysis).length && dataSource.length > 0
          }
          onChange={toggleSelectAll}
          sx={{
            padding: 0.5,
            "& .MuiSvgIcon-root": { fontSize: 20 },
            color: "white",
            "& .MuiSvgIcon-root path": {
              stroke: "white",
            },
            "&.Mui-checked": {
              color: "white",
            },
            "&.MuiCheckbox-indeterminate": {
              color: "white",
            },
            "& .MuiTouchRipple-root": {
              color: "white",
            },
          }}
        />
      </Box>
    ),
    [selectedIds, dataSource, toggleSelectAll]
  );

  const renderCellHandlerConfig = useMemo(
    () => ({
      select: (_: string, __: number, row: any) => renderCheckboxCell(row),
      creation_date: (_: string, __: number, row: any) => renderCreatedAtCell(row),
      type: (_: string, __: number, row: Forecast) => <ForecastTypeBadge type={row.type} />,
    }),
    [renderCheckboxCell, renderCreatedAtCell]
  );

  const renderSelectedForecasts = useCallback(() => {
    const forecasts = dataSource.filter((f) => selectedIds.includes(f.id));

    if (forecasts.length === 0) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "10px",
          border: "1px solid #E0E0E0",
          backgroundColor: "#FAFAFA",
          padding: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: 300,
          overflowY: "overlay",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {forecasts.map((f: any, idx: number) => (
          <Paper
            key={idx}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              border: "1px solid #E0E0E0",
              backgroundColor: "#FFFFFF",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: "40px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#212121",
                whiteSpace: "nowrap",
                fontSize: "14px",
                width: "150px",
                borderRight: "1px solid #E0E0E0",
                paddingRight: "12px",
              }}
            >
              {f.creation_date
                ? dayjs(f.creation_date).format("DD/MM/YYYY HH:mm")
                : ""}
            </Typography>

            <Typography
              sx={{
                flex: 1,
                fontSize: "14px",
                color: "#212121",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingLeft: "12px",
              }}
            >
              {f?.description || `Forecast ${f?.id}`}
            </Typography>

            {f.has_analysis && (
              <Typography
                variant="caption"
                sx={{ color: "#FF6F00", fontWeight: 600, marginLeft: 1 }}
              >
                {translate.t("has_analysis")}
              </Typography>
            )}
          </Paper>
        ))}
      </Paper>
    );
  }, [dataSource, selectedIds]);

  return (
    <section>
      <Loading open={isLoading} />
      <div className="floating-title" style={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h5">{translate.t("forecast")}</Typography>
      </div>

      <Grid item xs={12}>
        <Paper variant="outlined">
          <Box p={2}>
            <Formik
              enableReinitialize={true}
              initialValues={{ bomNumber: "" }}
              onSubmit={() => search()}
            >
              {() => (
                <Form autoComplete="off">
                  <Grid alignItems="center" container spacing={2} direction="row">
                    <Grid item xs={12} md={6}>
                      <Field
                        id="inp-forecast-description"
                        as={TextField}
                        name="search"
                        label={translate.t("search_description")}
                        fullWidth
                        variant="outlined"
                        value={searchDescription}
                        onChange={handleSearchChange}
                      />
                    </Grid>
                    <Grid item xs={12} md>
                      <FormControl fullWidth variant="outlined">
                        <AutocompleteSimple
                          id="inp-forecast-customer"
                          labelId="filter-customer"
                          service={serviceCustomer}
                          label={translate.t("customer")}
                          searchField="name"
                          display="name"
                          onSelectElement={handleSelectCustomer}
                          element={null}
                          clear={false}
                          reset={false}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md>
                      <FormControl fullWidth variant="outlined">
                        <AutocompleteSimple
                          id="inp-forecast-finished-good"
                          labelId="filter-finished-good"
                          service={serviceBom}
                          label={translate.t("finished_good")}
                          searchField="bom_number"
                          display="bom_number"
                          onSelectElement={handleSelectFinishedGood}
                          element={null}
                          clear={false}
                          reset={false}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item>
                      <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel id="forecast-type-filter-label">{translate.t("type")}</InputLabel>
                        <Select
                          labelId="forecast-type-filter-label"
                          value={forecastType}
                          label={translate.t("type")}
                          onChange={(event) => handleForecastTypeChange(event.target.value as ForecastType | "")}
                        >
                          <MenuItem value="">{translate.t("all")}</MenuItem>
                          {Object.values(ForecastType).map((value) => (
                            <MenuItem key={value} value={value}>{FORECAST_TYPE_LABELS[value]}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item>
                      <Fab
                        id="inp-forecast-search-submit"
                        size="small"
                        type="submit"
                        color="primary"
                        onClick={search}
                      >
                        <Search />
                      </Fab>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>

      <div>
        {selectedIds.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#FFF8E1",
              border: "1px solid #F8B653",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "16px",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ color: "#FBC02D" }} />
              <Typography sx={{ color: "#6D4C41", fontWeight: 500 }}>
                {selectedIds.length}{" "}
                {selectedIds.length === 1
                  ? translate.t("selected_forecast")
                  : translate.t("selected_forecasts")}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<Delete />}
              sx={{
                backgroundColor: "#3949AB",
                "&:hover": { backgroundColor: "#1F0063" },
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={() => {
                handleOpenDeleteModal(
                  dataSource.find((f) => f.id === selectedIds[0]) || ({} as Forecast),
                );
              }}
            >
              {translate.t("delete_item")}
            </Button>
          </Box>
        )}
      </div>

      <Paper variant="outlined" sx={{ marginTop: "16px" }}>
        <TablePaginationComponent
          id="table-forecast"
          state={dataSource}
          displayedColumnsShow={DISPLAYED_COLUMNS_SHOW}
          displayedColumns={DISPLAYED_COLUMNS}
          columnStyles={COLUMN_STYLES}
          showActions={SHOW_ACTIONS}
          dateColumns={DATE_COLUMNS}
          renderCellHandler={renderCellHandlerConfig}
          renderHeaderCheckbox={renderHeaderCheckbox}
          count={pagination?.count}
          handleDoubleClick={handleRowClick}
          next={pagination?.next}
          previous={pagination?.previous}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      {openDeleteModal && (
        <DialogVersion
          id="dialog-delete-forecast"
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          title={translate.t("delete")}
          message={confirmDeleteMessage}
          labelButton={translate.t("delete")}
          colorButton="error"
        >
          {renderSelectedForecasts()}
        </DialogVersion>
      )}
    </section>
  );
};

export default ForecastPageComponent;

